/**
 * Lazy loading with retry logic to handle "Failed to fetch dynamically imported module" errors.
 * This common error happens when a new version is deployed and old chunks are removed from the server.
 */
import { lazy, ComponentType } from 'react';

export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T } | T>
): ReturnType<typeof lazy> {
  return lazy(async () => {
    const pageHasAlreadyBeenReloaded = sessionStorage.getItem('page-reloaded');

    try {
      const component = await componentImport();

      // If we successfuly loaded the component, clear the reload flag
      sessionStorage.removeItem('page-reloaded');

      // Handle both { default: T } and just T (some imports might be structured differently)
      if ('default' in component) {
        return component as { default: T };
      }
      return { default: component };

    } catch (error) {
      console.error('Failed to load lazy component:', error);

      // If we haven't reloaded yet, try once
      if (!pageHasAlreadyBeenReloaded) {
        sessionStorage.setItem('page-reloaded', 'true');
        console.log('🔄 Chunk load failed. Force reloading page to fetch latest version...');
        window.location.reload();
      }

      // If we already reloaded and it still fails, throw the error
      throw error;
    }
  });
}
